import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  Button,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip
} from 'reactstrap';

import ReactTable from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { get } from 'lodash-es';
import moment from 'moment-timezone';
import ReactLoading from 'react-loading';
import axios from 'axios';

import CustomerTableCols from './CustomerTableCols';
import InlineAlert from '../../utilities/form-components/InlineAlert';
import { isProd } from '../../utilities/UrlPaths';

const errorLoadingText = `Unable to load your customers. Please try again later or contact 
  Customer Care any time at:`;

const Customer = () => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedGroupLabel, setSelectedGroupLabel] = useState('All Customers');
  const [groupsData, setGroupsData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorLoading, setErrorLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [sentEmailError, setSentEmailError] = useState(false);
  const { connectType, houseRef, houseUserName, houseID } = useSelector(state => state.house);

  //  refuse and redirect user missing the correct connectType
  const ycConnectTypes = ['s', 'p', 'e'];
  const refuseUser = ycConnectTypes.indexOf(connectType) === -1;

  document.title = 'Your Customers';

  useEffect(() => {
    const getCustomers = async () => {
      const apiUrl = (selectedGroup)
        ? `/app/house/${houseRef}/yourCustomers/group/${selectedGroup}`
        : `/app/house/${houseRef}/yourCustomers`;
      try {
        const authHeader = { Authorization: `${houseRef}*` };
        const customerResponse = await axios.get(apiUrl, { headers: authHeader });
        const customerObjectArray = [];
        const rawCustomerData = get(customerResponse, 'data.data.customers', []);
        const customerCount = rawCustomerData.length;
        let memberObject = {};

        if (rawCustomerData.length > 0) {
          rawCustomerData.forEach((member) => {
            //  APIs have different data structure
            if (selectedGroup) {
              const thisDate = new Date((member.creationDate.year + 1900), member.creationDate.month, member.creationDate.date);
              memberObject = {
                lastname: get(member, 'lastName', '').trim(),
                firstname: get(member, 'firstName', '').trim(),
                email: get(member, 'email', ''),
                phone: (member.address.phone !== null && member.address.phone !== '') ? member.address.phone : '',
                address: member.address.street1 || '',
                address2: (member.address.street2 !== 'null null' && member.address.street2 !== '') ? member.address.street2 : '',
                city: member.address.city || '',
                state: member.address.stateID || member.address.province || '',
                postcode: member.address.postalCode || '',
                country: member.address.countryName || '',
                accountCreated: moment(thisDate).format('MM/DD/YYYY') || '',
              };
            } else {
              const thisDate = new Date((member[12].year + 1900), member[12].month, member[12].date);
              memberObject = {
                lastname: (member[0] !== null && member[0] !== '') ? member[0].trim() : '',
                firstname: (member[1] !== null && member[1] !== '') ? member[1].trim() : '',
                email: (member[2] !== null && member[2] !== '') ? member[2] : '',
                phone: (member[3] !== null && member[3] !== '') ? member[3] : '',
                address: member[4] || '',
                address2: member[5] || '',
                city: member[7] || '',
                state: member[8] || '',
                postcode: member[9] || '',
                country: member[10] || '',
                accountCreated: moment(thisDate).format('MM/DD/YYYY') || '',
              };
            }
            customerObjectArray.push(memberObject);
          });
        }

        setCustomerData(customerObjectArray);
        setFilteredData(customerObjectArray);
        setTotalCustomers(customerCount);
        setSearchTerm('');
        setLoading(false);
      } catch (err) {
        setErrorLoading(true);
        setLoading(false);
        console.log(err.message);
      }
    };

    getCustomers();
  }, [houseRef, selectedGroup]);

  useEffect(() => {
    const getGroups = async () => {
      const groupsUrl = `/app/house/${houseRef}/groups`;
      const groupsResponse = await axios(groupsUrl);
      const groupsArray = get(groupsResponse, 'data.data.groups', []);
      setGroupsData(groupsArray);
    };

    getGroups();
  }, [houseRef]);

  const handleGroupSelection = (id, name) => {
    setSelectedGroup(id);
    setSelectedGroupLabel(name);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredData(customerData);
    setTotalCustomers(customerData.length);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    const filteredArray = customerData.filter(
      customer => customer.firstname.toLowerCase().includes(searchTerm.toLowerCase())
        || customer.lastname.toLowerCase().includes(searchTerm.toLowerCase())
        || customer.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredData(filteredArray);
    setTotalCustomers(filteredArray.length);
  };

  const exportCSV = () => {
    //  First the heders in CSV format
    let text = 'Firstname,Lastname,Address,Email,Phone,Account Created,\r\n';
    //  parse the filteredData to CSV.
    filteredData.forEach((customer) => {
      let addressBlock = customer.address;
      if (customer.address2) addressBlock += `,  ${customer.address2}`;
      if (customer.city || customer.state || customer.country) addressBlock += ',  ';
      if (customer.city) addressBlock += `${customer.city}`;
      if (customer.city && customer.state) addressBlock += `, ${customer.state}`;
      if (customer.postcode) addressBlock += ` ${customer.postcode}`;
      if (customer.country) addressBlock += ` ${customer.country}`;

      // Double quotes around addressBlock allow it to contain commas
      text += `${customer.firstname},${customer.lastname},"${addressBlock}",${customer.email},`;
      text += `${customer.phone},${customer.accountCreated}`;

      text += '\r\n';
    });
    //  this creates the pseudo element and opens a dialog box to save or open file
    const fileDate = moment(new Date()).format('YYYY-MM-DD');
    const element = document.createElement('a');
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`);
    element.setAttribute('download', `yourCustomers_${fileDate}.csv`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const customerSendResetEmail = async (email) => {
    try {
      const connectSuffix = (isProd) ? '' : '-stage';
      const apiHostname = `https://connect${connectSuffix}.invaluable.com/${houseUserName}`;

      const postBody = { email };
      const postHeaders = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          channel: 'infinitebidding',
          houseusername: houseUserName
        }
      };

      await axios.post(`${apiHostname}/app/v2/member/forgotPassword`, JSON.stringify(postBody), postHeaders);
      setEmailRecipient(email);
    } catch {
      setSentEmailError(true);
    }
  };

  const exportKeywords = async () => {
    const apiUrl = `/app/house/${houseRef}/keywordsReports`;
      try {
        const authHeader = { Authorization: `${houseRef}*` };
        const keywordData = await axios.get(apiUrl, { headers: authHeader });
        //  this creates the pseudo element and opens a dialog box to save or open file
        const fileDate = moment(new Date()).format('YYYY-MM-DD');
        const element = document.createElement('a');
        element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(keywordData.data)}`);
        element.setAttribute('download', `${houseUserName}_keywordAlerts_${fileDate}.csv`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      } catch {
        console.error('keyword download error')
      }
  };

  if (refuseUser) {
    return <Redirect to="/?p=1" />;
  }
  if (loading) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }
  if (errorLoading) {
    return (
      <Container className="py-4">
        <h1>Your Customers</h1>
        <InlineAlert baseText={errorLoadingText} />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row>
        <Col xs={6}>
          <h2>Your Private Label Customers</h2>
        </Col>
        <Col xs={6} className="align-right">
          <Button
            color="link"
            aria-label="Export to CSV"
            onClick={() => exportCSV()}
          >
            <span className="small">
              <FontAwesomeIcon icon="download" /> Export to CSV
            </span>
          </Button>
          <Button
            color="link"
            aria-label="Export to CSV"
            onClick={() => exportKeywords()}
            className="ml-4"
          >
            <span className="small">
              <FontAwesomeIcon icon="download" /> Keyword Alerts Report
            </span>
          </Button>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={3}>
          {totalCustomers} Customer{(totalCustomers !== 1) ? 's' : ''}
        </Col>
        <Col xs={4}>
          <InputGroup className="customers-search">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <FontAwesomeIcon icon="search" className="small" />
              </InputGroupText>
            </InputGroupAddon>
            <Input
              type="search"
              name="search"
              id="customerSearch"
              className="cancel-icon-search small-important"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  handleSearch(e);
                } else if ((e.key === 'Delete' || e.key === 'Backspace') && searchTerm.length === 0) {
                  clearSearch();
                }
              }}
            />
            <InputGroupAddon addonType="append">
              <InputGroupText className="cancel-button">
                <button
                  className="cancel-icon"
                  type="button"
                  aria-label="Cancel"
                  onClick={() => clearSearch()}
                >
                  <FontAwesomeIcon icon={faCircleXmark} className="color-gray-bg small" />
                </button>
              </InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </Col>
        <Col xs={5}>
          <Row>
            <Col xs={3} className="align-right pr-1 mt-1" id="saleGroupLabel">
              <Label for="saleGroup" className="small">
                Sale Group
              </Label>
              <FontAwesomeIcon className="ml-1 mt-1" icon="info-circle" />:
              <UncontrolledTooltip placement="right" target="saleGroupLabel">
                This page displays all customers who have created an account on your Private Label site by default.
                Filtering by sale group displays customers who have been approved for the selected sale.
              </UncontrolledTooltip>
            </Col>
            <Col xs={9} className="pl-0">
              <UncontrolledDropdown className="w-100">
                <DropdownToggle caret color="white" className="text-truncate small-important">
                  {selectedGroupLabel}
                </DropdownToggle>
                <DropdownMenu className="full-width-dropdown">
                  <DropdownItem
                    key={0}
                    className="w-100 small-important"
                    onClick={() => handleGroupSelection('', 'All Customers')}
                  >
                    All Customers
                  </DropdownItem>
                  {groupsData
                    && groupsData.map(group => (
                      <DropdownItem
                        key={group.groupID}
                        className="w-100 text-clamp-2 small-important"
                        onClick={() => handleGroupSelection(group.groupID, group.groupName)}
                      >
                        {group.groupName}
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </Col>
          </Row>
        </Col>
      </Row>
      <ReactTable
        className="-highlight small mt-4 w-100"
        minRows={1}
        data={filteredData}
        defaultPageSize={50}
        pageSizeOptions={[50, 100, 200]}
        columns={CustomerTableCols(customerSendResetEmail, houseID)}
        rowsText="per page"
        noDataText="No customers found in this group"
        showPageJump={false}
      />

      <Modal isOpen={emailRecipient.length > 0} centered>
        <ModalHeader className="pb-0 ml-4" toggle={() => setEmailRecipient('')} tag="h2">
          {sentEmailError ? 'Error' : 'Email Sent'}
        </ModalHeader>
        <ModalBody className="pt-0 mx-4 mb-4">
          <div>
            {
              sentEmailError
                ? 'There was an error sending the password email'
                : `A password reset email has been set to ${emailRecipient}`
            }
          </div>
        </ModalBody>
      </Modal>

    </Container>
  );
};

export default Customer;
